'use client';

import { App, trackEvent, useCurrentApp } from '@finn/ui-utils';
import { useCallback, useMemo, useRef, useState } from 'react';

import ModalContainer from '../../components/ModalContainer';
import { useModalStore } from '../../hooks/store';
import { useCosmicModule } from '../../hooks/useCosmicModule';
import { Deal, useDeal } from '../../hooks/useDeal';
import { usePreReturnAppraisal } from '../../hooks/usePreReturnAppraisal';
import { useReturn } from '../../hooks/useReturn';
import { useReturnMetadata } from '../../hooks/useReturnMetadata';
import { useDealId } from '../../providers/MetadataProvider';
import { Primitive } from '../../types';
import { PlatformComponentType } from '../../types/wizard';
import { ModalBuilder } from '../ModalBuilder';
import { ModalProps } from '../ModalBuilder/components/Modal';
import { StepWithOptionalSlug, WizardRef } from '../Wizard';
import { useUpdateReturnFormSubmit } from './hooks/useUpdateReturnSubmit';
import { appointmentStep } from './steps/appointmentStep';
import { contactStep } from './steps/contactStep';
import { locationStep } from './steps/locationStep';
import { constructReturnRequest, overviewStep } from './steps/overviewStep';
import { returnTypeSelectionStep } from './steps/returnTypeSelectionStep';

type ExtraStep = {
  id?: string;
  type?: 'replace' | 'insert' | 'delete';
  slug?: string;
  position: number;
  step?: (params: any) => StepWithOptionalSlug;
};

type EditReturnModalProps = ModalProps & {
  subscription?: Deal;
  PlatformComponent: PlatformComponentType;
  extraSteps?: ExtraStep[];
};

const MULTISTEP_EDIT_RETURN_MODAL = 'MULTISTEP_EDIT_RETURN';

export const EditReturnModal = (props: EditReturnModalProps) => {
  const dealId = useDealId();
  const content = useCosmicModule('advanced-edit-return');
  const { data: deal, mutate: mutateDeal } = useDeal(dealId);
  const { data: returnMetadata, status: availabilitiesLoadingStatus } =
    useReturnMetadata(dealId);
  const { data: selfReturnMetadata, status: selfReturnAvailabilitiesStatus } =
    useReturnMetadata(dealId, {
      returnType: 'SELF_RETURN',
      postcode: returnMetadata?.compound_locations?.[0]?.zip || null,
    });
  const { data: returnData, mutate: mutateReturn } = useReturn(dealId);
  const { mutate: mutatePreReturnAppraisal } = usePreReturnAppraisal(dealId);
  const shouldShowNotAvailableMessageForSelfReturn =
    selfReturnAvailabilitiesStatus === 'success' &&
    !selfReturnMetadata?.availabilities?.length;

  const availabilitiesIsLoading =
    availabilitiesLoadingStatus === 'loading' ||
    selfReturnAvailabilitiesStatus === 'loading';
  const [canSubmit, setCanSubmit] = useState(true);
  const { openModal, closeModal } = useModalStore();
  const { handleSubmit } = useUpdateReturnFormSubmit({
    success_toast_title: content?.toast?.success?.title,
    success_toast_description: content?.toast?.success?.description,
    error_toast_title: content?.toast?.error?.title,
    error_toast_description: content?.toast?.error?.description,
  });
  const app = useCurrentApp();

  const handleFormSubmit = useCallback(
    async (formData: { [key: string]: Primitive }) => {
      const payload = constructReturnRequest(formData);

      const status = await handleSubmit(payload);

      if (status === 200) {
        trackEvent('Edit Return Submitted', {
          continue_to_pre_return_appraisal_booking:
            formData.proceed_to_pre_return_appraisal_booking ? 'true' : null,
        });
        closeModal();
        mutateDeal();
        mutateReturn();
        mutatePreReturnAppraisal();

        if (
          formData.proceed_to_pre_return_appraisal_booking &&
          app !== App.B2B
        ) {
          openModal('onsite-appraisal-booking');
        } else if (app === App.B2B) {
          openModal('request-success-modal');
        }
      } else {
        openModal('request-error-modal');
      }
    },
    [
      app,
      handleSubmit,
      closeModal,
      mutateDeal,
      mutateReturn,
      mutatePreReturnAppraisal,
      openModal,
    ]
  );

  const wizardRef = useRef<WizardRef>(null);

  const steps = useMemo(() => {
    const nextSteps = [
      {
        ...returnTypeSelectionStep({
          content: {
            title: content?.location_selection?.title,
            footerCta: content?.location_selection?.cta,
          },
          shouldShowNotAvailableMessageForSelfReturn,
          returnMetadata,
          availabilitiesIsLoading,
        }),
        id: 'locationSelectionStep',
        slug: 'locationSelection',
      },
      {
        ...locationStep({
          content: content?.location,
          returnMetadata,
          returnData,
          availabilitiesIsLoading,
        }),
        id: 'locationStep',
        slug: 'location',
      },
      {
        ...appointmentStep({
          deal,
          title: content?.appointment?.title,
          footerCta: content?.appointment?.cta,
        }),
        id: 'appointmentStep',
        slug: 'appointment',
      },
      {
        ...contactStep({
          deal,
          content: content?.contact,
        }),
        id: 'contactStep',
        slug: 'contact',
      },
      {
        ...overviewStep({
          deal,
          title: content?.overview?.title,
          cta: content?.overview?.cta,
          canSubmit,
          setCanSubmit: (value) => setCanSubmit(value),
          compoundLocations: returnMetadata?.compound_locations,
        }),
        id: 'overviewStep',
        slug: 'overview',
      },
    ];

    if (props?.extraSteps?.length) {
      props.extraSteps.forEach((extraStep) => {
        if (extraStep?.step) {
          nextSteps.splice(
            extraStep.position,
            extraStep.type === 'replace' ? 1 : 0,
            {
              ...extraStep?.step({
                wizardRef,
                compoundLocations: returnMetadata?.compound_locations,
              }),
              id: extraStep.id || '',
              slug: extraStep.slug || '',
            }
          );
        } else if (extraStep.type === 'delete') {
          nextSteps.splice(extraStep.position, 1);
        }
      });
    }

    return nextSteps;
  }, [
    content,
    props?.extraSteps,
    shouldShowNotAvailableMessageForSelfReturn,
    returnMetadata,
    availabilitiesIsLoading,
    returnData,
    deal,
    canSubmit,
  ]);

  return (
    <ModalBuilder
      ref={wizardRef}
      PlatformComponent={props.PlatformComponent}
      open={props.open}
      onFormSubmit={handleFormSubmit}
      onClose={props.onClose}
      steps={steps}
      location={MULTISTEP_EDIT_RETURN_MODAL}
    />
  );
};

const renderNewEditReturnModal =
  (
    PlatformComponent: PlatformComponentType,
    { extraSteps }: { extraSteps?: ExtraStep[] }
  ) =>
  (open: boolean, onClose: () => void) => {
    return (
      <EditReturnModal
        extraSteps={extraSteps}
        PlatformComponent={PlatformComponent}
        open={open}
        onClose={onClose}
      />
    );
  };

export const EditReturnModalContainer = ({
  PlatformComponent,
  extraSteps,
}: {
  PlatformComponent: PlatformComponentType;
  extraSteps?: ExtraStep[];
}) => {
  const dealId = useDealId();

  if (!dealId) {
    return null;
  }

  return (
    <ModalContainer
      name={MULTISTEP_EDIT_RETURN_MODAL}
      renderModal={renderNewEditReturnModal(PlatformComponent, { extraSteps })}
    />
  );
};
